export default {
    controlHub: {
        eyebrow: "Operations overview",
        pageSubtitle:
            "If you are new here: complete the starter checklist, scan the three summaries, then work the priority table. Use filters to match your date range.",
        onboarding: {
            sectionTitle: "Your starter checklist",
            sectionSubtitle: "Fixed steps to connect EveMind to your workflow. Check items off as you go.",
            progressLabel: "{{pct}}% complete",
            markDone: "Mark as done: {{title}}",
            todos: {
                email: {
                    title: "Connect your company email in Profile → Integrations",
                    action: "Go to integrations",
                },
                profile: {
                    title: "Complete your company profile",
                    action: "Open profile",
                },
                container: {
                    title: "Create or register your first container",
                    action: "Create container",
                },
                document: {
                    title: "Open a container and view a document",
                    action: "Browse containers",
                },
                askEve: {
                    title: "Send your first question to Ask Eve",
                    action: "Open Ask Eve",
                },
            },
        },
        clusters: {
            sectionTitle: "Three summaries",
            docs: {
                eyebrow: "Documents & flow",
                title: "Are documents under control?",
                subtitle: "Share of shipments with complete documentation in this period.",
                primaryHint: "Higher is better — fewer missing or failed checks.",
                linePending: "Shipments with pending documents",
                lineDocIssues: "Open document issues",
                lineCrossMissing: "Missing docs + cross-check issues",
                cta: "View shipments with document issues",
            },
            risk: {
                eyebrow: "Risk",
                title: "Where is risk concentrated?",
                subtitle: "Share of shipments in elevated risk bands (medium and above).",
                primaryHint: "Lower is better — focus on high and critical first.",
                lineHighCritical: "High + critical (count)",
                lineMediumPlus: "Medium + high + critical (count)",
                cta: "View at-risk shipments",
            },
            friction: {
                eyebrow: "Logistics friction",
                title: "Where is operational friction?",
                subtitle: "Non-document issues and active shipments — signals of delays and exceptions.",
                primaryHint: "Higher non-document issues means more operational exceptions to clear.",
                lineInTransit: "In transit (shipments)",
                lineAboutToDepart: "About to depart (ETD within 2 days, booked)",
                lineReviewCenter: "Review Center (pending items)",
                lineAllIssues: "All active issues (total)",
                topRoute: "Top lane (by activity): {{route}}",
                ctaIssues: "Open issues",
                ctaReview: "Open Review Center",
            },
        },
        ledger: {
            title: "Priority shipments (by value at risk)",
            description:
                "These are the highest-impact containers in the selected period. Open a row to work documents, issues, and timeline on the shipment page.",
        },
        companySnapshot: {
            title: "Company snapshot",
            subtitle: "Background context on your trade lanes and commodities — use this after you have handled the priorities above.",
        },
        hubCompanySnapshot: {
            title: "How you operate",
            subtitle: "Lightweight context on your profile — open the full profile for details.",
            loadError: "Could not load company profile.",
            metricActivePartners: "Active partners",
            metricTotalContainers: "Containers (lifetime)",
            metricTotalContainersFiltered: "Containers (selected period)",
            metricUniqueRoutes: "Unique routes",
            metricCommodities: "Commodities",
            topRoutesTitle: "Top routes",
            colRoute: "Route",
            colShipments: "Shipments",
            colValue: "Value",
            openProfile: "Open full profile",
        },
        toolbar: {
            clear: "Clear",
            carrierPlaceholder: "Carrier",
            statusPlaceholder: "Status",
            allCarriers: "All carriers",
            allStatuses: "All statuses",
            includeMediumRisk: "Include medium risk",
        },
        quickStats: {
            title: "Key counts ({{start}} – {{end}})",
            titleNoRange: "Key counts",
            subtitle: "Shortcuts to filtered lists. Details sit in the summaries and table above.",
        },
        tabs: {
            containerSummary: "Container Summary",
            companyStats: "Company Stats",
        },
        timeRange: {
            thisMonth: "This Month",
            last3Months: "Last 3 Months",
            last6Months: "Last 6 Months",
            last9Months: "Last 9 Months",
            thisYear: "This Year",
        },
        stats: {
            activePartners: "Active Partners",
            teamMembers: "Team Members",
            pendingReviews: "Review Center - pending items",
        },
        tooltips: {
            docIssues: {
                title: "Containers with Document Issues",
                description: "Number of containers that have at least one active document issue (missing docs, check failures, cross-check errors)",
            },
            issues: {
                title: "Issues",
                description: "Total active issues across all containers",
            },
            atRisk: {
                title: "At Risk Shipments",
                description: "Containers with risk score ≥ 25% (medium, high, critical)",
            },
            highRisk: {
                title: "High Risk Shipments",
                description: "Containers with risk score ≥ 50% requiring attention",
            },
            activePartners: {
                title: "Active Trade Partners",
                description: "Companies you have active shipments with (consignees, freight forwarders)",
            },
            teamMembers: {
                title: "Active Team Members",
                description: "Users in your organization with access to this workspace",
            },
            pendingReviews: {
                title: "Pending Review Items",
                description:
                    "Items awaiting human review — container identification, document assignment, tracking checks",
            },
        },
        coordinationHub: {
            title: "Where to look first",
            subtitle: "Three different places for updates, risks, and human checks — jump in from here.",
            glossaryLink: "What’s the difference?",
            glossaryTitle: "Activity, Issues, and Review Center",
            cards: {
                activity: "Activity",
                activityHint: "Feed and email-related updates",
                issues: "Issues",
                issuesHint: "Open problems on shipments and documents",
                review: "Review Center",
                reviewHint: "Items that need a human decision",
                unread: "unread in feed",
                open: "open issues",
                pending: "pending reviews",
            },
            glossary: {
                activityTerm: "Activity",
                activityBody:
                    "Your chronological feed: trade communications, system notices, and unread markers for what changed recently.",
                issuesTerm: "Issues",
                issuesBody:
                    "Structured problems tied to containers or documents (missing docs, checks, delays). This is your operational exception queue.",
                reviewTerm: "Review Center",
                reviewBody:
                    "A short queue for human confirmation (e.g. document assignment, identification). Different from the general activity feed.",
            },
        },
        firstSession: {
            title: "First steps in EveMind",
            dismiss: "Dismiss",
            stepEmail: "Connect your email (Integrations in Profile)",
            stepContainer: "Open a container from the list to see documents, timeline, and issues",
            stepReview: "Check Review Center when you have pending reviews",
        },
        riskyShipments: {
            title: "Risky Shipments",
            description: "High-risk containers sorted by value at risk",
            emptyState: "No high-risk shipments found",
            colStatus: "Status",
            viewAll: "View all",
        },
        companyProfile: {
            title: "Company Profile",
            profileCompleteness: "Profile Completeness",
            tradingPattern: "Trading Pattern",
            volumeTier: "Volume Tier",
            commodities: "Commodities",
            tradeRoutes: "Trade Routes",
            partners: "Trade Partners",
            containers: "Container Activity",
            topHsCodes: "Top HS Codes",
            topRoutes: "Top Trade Routes",
        },
    },
    admin: {
        title: "Dashboard",
        subtitle: "System overview and account management",
        newAccount: "New Account",
        loading: "Loading dashboard...",
        stats: {
            totalAccounts: "Total Accounts",
            activeAccounts: "Active Accounts",
            newThisMonth: "New This Month",
            suspendedAccounts: "Suspended Accounts",
        },
        recentAccounts: {
            title: "Recent Accounts",
            description: "Last 5 accounts created in the system",
            viewAll: "View all",
            headers: {
                userType: "User Type",
                legalName: "Legal Name",
                entityType: "Entity Type",
                country: "Country",
                status: "Status",
                createdAt: "Creation Date",
            },
            status: {
                active: "Active",
                suspended: "Suspended",
                inactive: "Inactive",
            },
        },
    },
    sender: {
        title: "Dashboard",
        subtitle: "Overview of containers and collaborations",
        loading: "Loading dashboard...",
        stats: {
            totalContainers: "Total Containers",
            inTransit: "In Transit",
            delivered: "Delivered",
            delayed: "Delayed",
            pendingDocuments: "Pending Documents",
            highRisk: "High Risk",
        },
        collaborations: {
            title: "Collaborations",
            total: "Total",
            active: "Active",
            pending: "Pending",
        },
        team: {
            title: "Team",
            total: "Total",
            active: "Active",
        },
        recentContainers: {
            title: "Recent Containers",
            description: "Last 5 containers created",
            viewAll: "View all",
            headers: {
                containerId: "Container Ref.",
                route: "Route",
                progress: "Progress",
                eta: "ETA",
                docStatus: "Doc Status",
                riskLevel: "Risk Level",
                value: "Value",
            },
        },
    },
    shipper: {
        title: "Dashboard",
        subtitle: "Overview of containers and collaborations",
        loading: "Loading dashboard...",
        stats: {
            totalContainers: "Total Containers",
            inTransit: "In Transit",
            delivered: "Delivered",
            delayed: "Delayed",
            pendingDocuments: "Pending Documents",
            highRisk: "High Risk",
            containersWithDocIssues: "With Doc Issues",
            issues: "With Issues",
            atRisk: "At Risk",
        },
        collaborations: {
            title: "Collaborations",
            total: "Total",
            active: "Active",
            pending: "Pending",
        },
        team: {
            title: "Team",
            total: "Total",
            active: "Active",
        },
        recentContainers: {
            title: "Recent Containers",
            description: "Last 5 containers created",
            viewAll: "View all",
            headers: {
                containerId: "Container Ref.",
                route: "Route",
                progress: "Progress",
                eta: "ETA",
                docStatus: "Doc Status",
                riskLevel: "Risk Level",
                value: "Value",
            },
        },
    },
    receiver: {
        title: "Dashboard",
        subtitle: "Overview of received containers and collaborations",
        loading: "Loading dashboard...",
        newOrderRequest: "New Order Request",
        manageShippers: "Manage Shippers",
        viewContainers: "View Containers",
        newOrderModal: {
            title: "New Order Request",
            shipper: "Shipper",
            shipperPlaceholder: "Select a shipper",
            targetWeek: "Target Week",
            packingList: "Requested Packing List",
            packingListPlaceholder: "Describe in detail the requested packing list...",
            notes: "Notes",
            notesPlaceholder: "Additional notes about the order...",
            submit: "Create Order"
        },
        stats: {
            totalContainers: "Total Containers",
            inTransit: "In Transit",
            delivered: "Delivered",
            delayed: "Delayed",
            pendingDocuments: "Pending Documents",
            highRisk: "High Risk",
        },
        collaborations: {
            title: "Collaborations",
            total: "Total",
            active: "Active",
            pending: "Pending",
        },
        team: {
            title: "Team",
            total: "Total",
            active: "Active",
        },
        recentContainers: {
            title: "Recent Containers",
            description: "Last 5 containers received",
            viewAll: "View all",
            headers: {
                containerId: "Container Ref.",
                shipper: "Shipper",
                route: "Route",
                progress: "Progress",
                docStatus: "Doc Status",
                riskLevel: "Risk Level",
            },
        },
        monthlyTrend: {
            title: "Monthly Trend",
        },
        trendVsLastMonth: "vs last month",
        receivedContainers: "Received Containers",
        activeShippers: "Active Shippers",
        charts: {
            commodityDistribution: "Commodity Distribution",
            originCountries: "Origin Countries",
            arrivalPorts: "Arrival Ports",
        },
    },
};
